<template>
  <div class="study-planning">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">学习规划</h1>
        <div class="header-actions">
          <el-button type="primary" @click="createNewPlan">
            <i class="fa fa-plus"></i> 创建新计划
          </el-button>
        </div>
      </div>
    </div>

    <!-- 当前计划概览 -->
    <div v-if="currentPlan" class="current-plan">
      <div class="plan-header">
        <div class="plan-info">
          <h2 class="plan-title">{{ currentPlan.title }}</h2>
          <div class="plan-meta">
            <span class="plan-period">{{ formatDate(currentPlan.startDate) }} - {{ formatDate(currentPlan.endDate) }}</span>
            <span class="plan-status" :class="currentPlan.status">{{ getPlanStatusText(currentPlan.status) }}</span>
          </div>
        </div>
        <div class="plan-progress">
          <div class="progress-circle">
            <svg width="120" height="120">
              <circle
                cx="60"
                cy="60"
                r="50"
                fill="none"
                stroke="#e6e6e6"
                stroke-width="8"
              />
              <circle
                cx="60"
                cy="60"
                r="50"
                fill="none"
                stroke="#3498db"
                stroke-width="8"
                stroke-linecap="round"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="circumference - (currentPlan.progress / 100) * circumference"
                transform="rotate(-90 60 60)"
              />
            </svg>
            <div class="progress-text">
              <span class="progress-value">{{ currentPlan.progress }}%</span>
              <span class="progress-label">完成度</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 目标对比 -->
      <div class="goal-comparison">
        <div class="goal-item">
          <div class="goal-label">当前成绩</div>
          <div class="goal-value current">{{ currentPlan.currentScore }}分</div>
        </div>
        <div class="goal-arrow">
          <i class="fa fa-arrow-right"></i>
        </div>
        <div class="goal-item">
          <div class="goal-label">目标成绩</div>
          <div class="goal-value target">{{ currentPlan.targetScore }}分</div>
        </div>
        <div class="goal-item">
          <div class="goal-label">预计提升</div>
          <div class="goal-value improvement">+{{ currentPlan.targetScore - currentPlan.currentScore }}分</div>
        </div>
      </div>

      <!-- 里程碑进度 -->
      <div class="milestones">
        <h3>阶段目标</h3>
        <div class="milestone-list">
          <div 
            v-for="(milestone, index) in currentPlan.milestones" 
            :key="index"
            class="milestone-item"
            :class="{ 
              completed: milestone.completed,
              current: milestone.current,
              upcoming: !milestone.completed && !milestone.current
            }"
          >
            <div class="milestone-icon">
              <i class="fa" :class="getMilestoneIcon(milestone)"></i>
            </div>
            <div class="milestone-content">
              <div class="milestone-title">{{ milestone.title }}</div>
              <div class="milestone-desc">{{ milestone.description }}</div>
              <div class="milestone-date">{{ formatDate(milestone.targetDate) }}</div>
            </div>
            <div class="milestone-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: milestone.progress + '%' }"></div>
              </div>
              <span class="progress-text">{{ milestone.progress }}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 无计划状态 -->
    <div v-else class="no-plan">
      <div class="no-plan-content">
        <div class="no-plan-icon">
          <i class="fa fa-calendar-plus"></i>
        </div>
        <h3>还没有学习计划</h3>
        <p>创建一个学习计划，让我们帮你制定科学的学习路径</p>
        <el-button type="primary" size="large" @click="createNewPlan">
          创建学习计划
        </el-button>
      </div>
    </div>

    <!-- 学习统计 -->
    <div class="study-stats">
      <div class="stats-header">
        <h3>学习统计</h3>
        <div class="time-filter">
          <el-radio-group v-model="statsTimeRange" @change="loadStudyStats">
            <el-radio-button label="week">本周</el-radio-button>
            <el-radio-button label="month">本月</el-radio-button>
            <el-radio-button label="quarter">本季度</el-radio-button>
          </el-radio-group>
        </div>
      </div>
      
      <div class="stats-content">
        <!-- 学习时长统计 -->
        <div class="stat-card">
          <div class="stat-header">
            <h4>学习时长趋势</h4>
          </div>
          <div class="chart-container">
            <div ref="studyTimeChart" class="chart"></div>
          </div>
        </div>

        <!-- 成绩变化 -->
        <div class="stat-card">
          <div class="stat-header">
            <h4>成绩变化</h4>
          </div>
          <div class="chart-container">
            <div ref="scoreChart" class="chart"></div>
          </div>
        </div>

        <!-- 知识点掌握情况 -->
        <div class="stat-card">
          <div class="stat-header">
            <h4>知识点掌握情况</h4>
          </div>
          <div class="knowledge-stats">
            <div 
              v-for="knowledge in knowledgeStats" 
              :key="knowledge.id"
              class="knowledge-item"
            >
              <div class="knowledge-name">{{ knowledge.name }}</div>
              <div class="knowledge-progress">
                <div class="progress-bar">
                  <div 
                    class="progress-fill" 
                    :style="{ 
                      width: knowledge.mastery + '%',
                      backgroundColor: getKnowledgeColor(knowledge.mastery)
                    }"
                  ></div>
                </div>
                <span class="progress-value">{{ knowledge.mastery }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 历史计划 -->
    <div class="history-plans">
      <div class="section-header">
        <h3>历史计划</h3>
        <el-button type="text" @click="showAllPlans = !showAllPlans">
          {{ showAllPlans ? '收起' : '查看全部' }}
        </el-button>
      </div>
      
      <div class="plan-list" v-show="showAllPlans">
        <div 
          v-for="plan in historyPlans" 
          :key="plan.id"
          class="plan-card"
          @click="viewPlanDetail(plan)"
        >
          <div class="plan-info">
            <h4 class="plan-title">{{ plan.title }}</h4>
            <div class="plan-meta">
              <span class="plan-period">{{ formatDate(plan.startDate) }} - {{ formatDate(plan.endDate) }}</span>
              <span class="plan-status" :class="plan.status">{{ getPlanStatusText(plan.status) }}</span>
            </div>
          </div>
          <div class="plan-result">
            <div class="result-item">
              <span class="result-label">完成度</span>
              <span class="result-value">{{ plan.progress }}%</span>
            </div>
            <div class="result-item">
              <span class="result-label">成绩提升</span>
              <span class="result-value">+{{ plan.actualImprovement || 0 }}分</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 创建计划对话框 -->
    <el-dialog
      title="创建学习计划"
      :visible.sync="createPlanVisible"
      width="600px"
      center
    >
      <el-form :model="newPlan" :rules="planRules" ref="planForm" label-width="100px">
        <el-form-item label="计划名称" prop="title">
          <el-input v-model="newPlan.title" placeholder="请输入计划名称"></el-input>
        </el-form-item>
        
        <el-form-item label="当前成绩" prop="currentScore">
          <el-input-number 
            v-model="newPlan.currentScore" 
            :min="0" 
            :max="100" 
            placeholder="请输入当前成绩"
          ></el-input-number>
        </el-form-item>
        
        <el-form-item label="目标成绩" prop="targetScore">
          <el-input-number 
            v-model="newPlan.targetScore" 
            :min="0" 
            :max="100" 
            placeholder="请输入目标成绩"
          ></el-input-number>
        </el-form-item>
        
        <el-form-item label="计划周期" prop="duration">
          <el-date-picker
            v-model="planDateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
          ></el-date-picker>
        </el-form-item>
        
        <el-form-item label="学习重点">
          <el-checkbox-group v-model="newPlan.focusAreas">
            <el-checkbox label="错题复习">错题复习</el-checkbox>
            <el-checkbox label="薄弱知识点">薄弱知识点</el-checkbox>
            <el-checkbox label="专项练习">专项练习</el-checkbox>
            <el-checkbox label="模拟测试">模拟测试</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        
        <el-form-item label="每日学习时长">
          <el-slider
            v-model="newPlan.dailyStudyTime"
            :min="30"
            :max="300"
            :step="30"
            show-stops
            :format-tooltip="formatStudyTime"
          ></el-slider>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="createPlanVisible = false">取消</el-button>
        <el-button type="primary" @click="submitPlan" :loading="submitting">创建计划</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { axios } from "../../config/axios";
import moment from 'moment';
import * as echarts from 'echarts';

export default {
  name: 'StudyPlanning',
  data() {
    return {
      // 当前计划
      currentPlan: null,
      circumference: 2 * Math.PI * 50, // 圆周长
      
      // 统计数据
      statsTimeRange: 'week',
      knowledgeStats: [],
      
      // 历史计划
      historyPlans: [],
      showAllPlans: false,
      
      // 创建计划
      createPlanVisible: false,
      submitting: false,
      planDateRange: [],
      newPlan: {
        title: '',
        currentScore: null,
        targetScore: null,
        focusAreas: [],
        dailyStudyTime: 120
      },
      planRules: {
        title: [
          { required: true, message: '请输入计划名称', trigger: 'blur' }
        ],
        currentScore: [
          { required: true, message: '请输入当前成绩', trigger: 'blur' }
        ],
        targetScore: [
          { required: true, message: '请输入目标成绩', trigger: 'blur' }
        ]
      },
      
      // 图表实例
      studyTimeChart: null,
      scoreChart: null
    }
  },
  created() {
    this.loadCurrentPlan();
    this.loadHistoryPlans();
    this.loadStudyStats();
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts();
    });
  },
  beforeDestroy() {
    if (this.studyTimeChart) {
      this.studyTimeChart.dispose();
    }
    if (this.scoreChart) {
      this.scoreChart.dispose();
    }
  },
  methods: {
    async loadCurrentPlan() {
      try {
        const response = await axios.get('/student/current-plan');
        this.currentPlan = response.data.data;
      } catch (error) {
        console.error('加载当前计划失败:', error);
      }
    },
    
    async loadHistoryPlans() {
      try {
        const response = await axios.get('/student/history-plans');
        this.historyPlans = response.data.data || [];
      } catch (error) {
        console.error('加载历史计划失败:', error);
      }
    },
    
    async loadStudyStats() {
      try {
        const response = await axios.get('/student/study-stats', {
          params: { timeRange: this.statsTimeRange }
        });
        const data = response.data.data;
        this.knowledgeStats = data.knowledgeStats || [];
        
        // 更新图表数据
        this.updateCharts(data);
        
      } catch (error) {
        console.error('加载学习统计失败:', error);
      }
    },
    
    initCharts() {
      // 初始化学习时长图表
      if (this.$refs.studyTimeChart) {
        this.studyTimeChart = echarts.init(this.$refs.studyTimeChart);
      }
      
      // 初始化成绩图表
      if (this.$refs.scoreChart) {
        this.scoreChart = echarts.init(this.$refs.scoreChart);
      }
    },
    
    updateCharts(data) {
      // 更新学习时长图表
      if (this.studyTimeChart && data.studyTimeData) {
        const option = {
          tooltip: {
            trigger: 'axis'
          },
          xAxis: {
            type: 'category',
            data: data.studyTimeData.dates
          },
          yAxis: {
            type: 'value',
            name: '学习时长(分钟)'
          },
          series: [{
            data: data.studyTimeData.values,
            type: 'line',
            smooth: true,
            itemStyle: {
              color: '#3498db'
            }
          }]
        };
        this.studyTimeChart.setOption(option);
      }
      
      // 更新成绩图表
      if (this.scoreChart && data.scoreData) {
        const option = {
          tooltip: {
            trigger: 'axis'
          },
          xAxis: {
            type: 'category',
            data: data.scoreData.dates
          },
          yAxis: {
            type: 'value',
            name: '成绩',
            min: 0,
            max: 100
          },
          series: [{
            data: data.scoreData.values,
            type: 'line',
            smooth: true,
            itemStyle: {
              color: '#27ae60'
            }
          }]
        };
        this.scoreChart.setOption(option);
      }
    },
    
    createNewPlan() {
      this.createPlanVisible = true;
      this.resetPlanForm();
    },
    
    resetPlanForm() {
      this.newPlan = {
        title: '',
        currentScore: null,
        targetScore: null,
        focusAreas: [],
        dailyStudyTime: 120
      };
      this.planDateRange = [];
      if (this.$refs.planForm) {
        this.$refs.planForm.resetFields();
      }
    },
    
    async submitPlan() {
      try {
        await this.$refs.planForm.validate();
        
        if (!this.planDateRange || this.planDateRange.length !== 2) {
          this.$message.error('请选择计划周期');
          return;
        }
        
        if (this.newPlan.targetScore <= this.newPlan.currentScore) {
          this.$message.error('目标成绩应该高于当前成绩');
          return;
        }
        
        this.submitting = true;
        
        const planData = {
          ...this.newPlan,
          startDate: moment(this.planDateRange[0]).format('YYYY-MM-DD'),
          endDate: moment(this.planDateRange[1]).format('YYYY-MM-DD')
        };
        
        await axios.post('/student/create-plan', planData);
        
        this.$message.success('学习计划创建成功');
        this.createPlanVisible = false;
        this.loadCurrentPlan();
        
      } catch (error) {
        console.error('创建计划失败:', error);
        this.$message.error('创建计划失败');
      } finally {
        this.submitting = false;
      }
    },
    
    viewPlanDetail(plan) {
      this.$router.push(`/student/plan-detail/${plan.id}`);
    },
    
    getMilestoneIcon(milestone) {
      if (milestone.completed) return 'fa-check-circle';
      if (milestone.current) return 'fa-play-circle';
      return 'fa-circle-o';
    },
    
    getPlanStatusText(status) {
      const statusMap = {
        active: '进行中',
        completed: '已完成',
        paused: '已暂停',
        cancelled: '已取消'
      };
      return statusMap[status] || status;
    },
    
    getKnowledgeColor(mastery) {
      if (mastery >= 80) return '#27ae60';
      if (mastery >= 60) return '#f39c12';
      return '#e74c3c';
    },
    
    formatDate(date) {
      return moment(date).format('YYYY-MM-DD');
    },
    
    formatStudyTime(value) {
      const hours = Math.floor(value / 60);
      const minutes = value % 60;
      if (hours > 0) {
        return `${hours}小时${minutes > 0 ? minutes + '分钟' : ''}`;
      }
      return `${minutes}分钟`;
    }
  }
}
</script>

<style lang="less" scoped>
.study-planning {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

// 页面头部
.page-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}

// 当前计划
.current-plan {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.plan-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.plan-info {
  flex: 1;
}

.plan-title {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
}

.plan-meta {
  display: flex;
  gap: 16px;
  align-items: center;
}

.plan-period {
  color: #7f8c8d;
  font-size: 14px;
}

.plan-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  &.active {
    background-color: #e3f2fd;
    color: #1976d2;
  }
  
  &.completed {
    background-color: #e8f5e9;
    color: #2e7d32;
  }
  
  &.paused {
    background-color: #fff3e0;
    color: #e65100;
  }
  
  &.cancelled {
    background-color: #ffebee;
    color: #c62828;
  }
}

// 进度圆环
.progress-circle {
  position: relative;
  width: 120px;
  height: 120px;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.progress-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: #3498db;
}

.progress-label {
  font-size: 12px;
  color: #7f8c8d;
}

// 目标对比
.goal-comparison {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  margin-bottom: 32px;
  padding: 24px;
  background-color: #f8f9fa;
  border-radius: 12px;
}

.goal-item {
  text-align: center;
}

.goal-label {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 8px;
}

.goal-value {
  font-size: 28px;
  font-weight: 700;
  
  &.current {
    color: #3498db;
  }
  
  &.target {
    color: #27ae60;
  }
  
  &.improvement {
    color: #e67e22;
  }
}

.goal-arrow {
  font-size: 24px;
  color: #95a5a6;
}

// 里程碑
.milestones {
  h3 {
    margin-bottom: 20px;
    color: #2c3e50;
  }
}

.milestone-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.milestone-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 8px;
  transition: all 0.2s;
  
  &.completed {
    background-color: #e8f5e9;
    border-left: 4px solid #27ae60;
    
    .milestone-icon i {
      color: #27ae60;
    }
  }
  
  &.current {
    background-color: #e3f2fd;
    border-left: 4px solid #3498db;
    
    .milestone-icon i {
      color: #3498db;
    }
  }
  
  &.upcoming {
    background-color: #f8f9fa;
    border-left: 4px solid #e0e0e0;
    
    .milestone-icon i {
      color: #95a5a6;
    }
  }
}

.milestone-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  
  i {
    font-size: 20px;
  }
}

.milestone-content {
  flex: 1;
  margin-right: 16px;
}

.milestone-title {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
}

.milestone-desc {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 4px;
}

.milestone-date {
  font-size: 12px;
  color: #95a5a6;
}

.milestone-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 120px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #3498db;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #7f8c8d;
  min-width: 35px;
}

// 无计划状态
.no-plan {
  background: white;
  border-radius: 12px;
  padding: 60px 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.no-plan-icon {
  font-size: 64px;
  color: #bdc3c7;
  margin-bottom: 24px;
}

.no-plan-content {
  h3 {
    margin-bottom: 12px;
    color: #2c3e50;
  }
  
  p {
    color: #7f8c8d;
    margin-bottom: 24px;
  }
}

// 学习统计
.study-stats {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  h3 {
    margin: 0;
    color: #2c3e50;
  }
}

.stats-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
}

.stat-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
}

.stat-header {
  margin-bottom: 16px;
  
  h4 {
    margin: 0;
    color: #2c3e50;
  }
}

.chart-container {
  height: 200px;
}

.chart {
  width: 100%;
  height: 100%;
}

// 知识点统计
.knowledge-stats {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.knowledge-item {
  display: flex;
  align-items: center;
  gap: 16px;
}

.knowledge-name {
  min-width: 100px;
  font-weight: 500;
  color: #2c3e50;
}

.knowledge-progress {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-value {
  font-size: 14px;
  color: #7f8c8d;
  min-width: 40px;
}

// 历史计划
.history-plans {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h3 {
    margin: 0;
    color: #2c3e50;
  }
}

.plan-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.plan-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #e9ecef;
  }
}

.plan-card .plan-info {
  flex: 1;
}

.plan-card .plan-title {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 500;
  color: #2c3e50;
}

.plan-result {
  display: flex;
  gap: 24px;
}

.result-item {
  text-align: center;
}

.result-label {
  display: block;
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 4px;
}

.result-value {
  font-size: 16px;
  font-weight: 600;
  color: #3498db;
}

// 响应式设计
@media (max-width: 1200px) {
  .stats-content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .study-planning {
    padding: 15px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 16px;
  }
  
  .plan-header {
    flex-direction: column;
    gap: 24px;
    text-align: center;
  }
  
  .goal-comparison {
    flex-direction: column;
    gap: 16px;
  }
  
  .goal-arrow {
    transform: rotate(90deg);
  }
  
  .milestone-item {
    flex-direction: column;
    text-align: center;
  }
  
  .milestone-progress {
    width: 100%;
    justify-content: center;
  }
  
  .plan-card {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .stats-content {
    grid-template-columns: 1fr;
  }
  
  .chart-container {
    height: 150px;
  }
  
  .knowledge-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .knowledge-progress {
    width: 100%;
  }
}
</style>
